/*
 * Mission Watch — Public read-only shared report view.
 *
 * Route:  /r/:token
 * Data:   GET /api/public/report/{token} (no auth)
 * Purpose: a board member, funder, donor, or auditor can open this link and see
 *          the exact Mission Watch Score & report state as of a given date without
 *          needing a login. No user email is disclosed; no internal ids leak.
 */
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ShieldCheck, Sparkles, Lightbulb, CheckCircle2, AlertTriangle, XCircle, MinusCircle, Award, Clock, ExternalLink,
} from "lucide-react";
import api from "../lib/api";


const SCORE_STYLES = {
  green: "bg-emerald-50 border-emerald-100 text-emerald-600",
  amber: "bg-amber-50 border-amber-100 text-amber-600",
  red: "bg-red-50 border-red-100 text-red-600",
  info: "bg-charcoal-100 border-black/5 text-charcoal-400",
};


function StatusIcon({ status }) {
  if (status === "green") return <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" strokeWidth={2} />;
  if (status === "amber") return <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0" strokeWidth={2} />;
  if (status === "red") return <XCircle className="w-4 h-4 text-red-500 shrink-0" strokeWidth={2} />;
  return <MinusCircle className="w-4 h-4 text-charcoal-300 shrink-0" strokeWidth={2} />;
}


function fmt(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-US", {
      month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit",
    });
  } catch { return iso; }
}


export default function PublicReport() {
  const { token } = useParams();
  const [report, setReport] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    // Public endpoint — no auth. Use the shared axios instance so baseURL is respected.
    api.get(`/public/report/${token}`)
      .then(({ data }) => setReport(data))
      .catch(() => setError(true));
  }, [token]);

  if (error) {
    return (
      <div className="min-h-screen bg-charcoal-50 flex items-center justify-center p-6" data-testid="public-report-notfound">
        <div className="max-w-md text-center rounded-2xl bg-white border border-black/5 shadow-soft p-10">
          <div className="w-12 h-12 rounded-full bg-charcoal-100 mx-auto grid place-items-center mb-4">
            <XCircle className="w-6 h-6 text-charcoal-400" strokeWidth={1.75} />
          </div>
          <h1 className="text-lg font-semibold text-charcoal-900">Report not found</h1>
          <p className="text-sm text-charcoal-500 mt-2">
            This share link isn't valid — the owner may have turned sharing off, or the link was mistyped.
          </p>
          <Link to="/" className="inline-flex mt-6 items-center gap-1.5 text-sm text-brand hover:underline">
            Learn about Mission Watch <ExternalLink className="w-3.5 h-3.5" />
          </Link>
        </div>
      </div>
    );
  }
  if (!report) {
    return (
      <div className="min-h-screen bg-charcoal-50 flex items-center justify-center">
        <p className="text-sm text-charcoal-400 font-mono">Loading…</p>
      </div>
    );
  }
  return (
    <div className="min-h-screen bg-charcoal-50" data-testid="public-report">
      {/* header */}
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/80 border-b border-black/5">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-charcoal-900 grid place-items-center">
              <ShieldCheck className="w-4 h-4 text-emerald-400" />
            </div>
            <span className="font-semibold tracking-tight text-charcoal-900 text-[15px]">Mission Watch</span>
          </Link>
          <Link to="/" className="text-xs sm:text-sm text-brand hover:underline inline-flex items-center gap-1" data-testid="public-learn-more">
            What is this? <ExternalLink className="w-3.5 h-3.5" />
          </Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-5 sm:px-8 py-10">
        {/* Public share disclosure */}
        <div className="mb-6 rounded-full bg-emerald-50 border border-emerald-100 px-4 py-1.5 inline-flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.15em] text-emerald-700" data-testid="public-badge">
          <Award className="w-3.5 h-3.5" /> Mission Watch Verified · Shared Report
        </div>

        <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight text-charcoal-900">
          {report.organization_name}
        </h1>
        <p className="text-sm text-charcoal-500 mt-2">
          {report.period || fmt(report.generated_at)} · {report.scan_depth === "deep"
            ? `Whole-site scan (${report.pages_scanned || "?"} pages)`
            : "Standard scan"}
        </p>

        {/* hero score */}
        <section className="mt-8 rounded-2xl bg-white border border-black/5 shadow-soft p-6 sm:p-8 flex flex-wrap items-center justify-between gap-6">
          <div>
            <p className="text-[11px] font-mono uppercase tracking-[0.15em] text-charcoal-400">Mission Watch Score</p>
            <div className="mt-2 flex items-baseline gap-2">
              <span className="text-6xl font-semibold tracking-tighter text-charcoal-900">{report.overall_score ?? "—"}</span>
              <span className="text-base text-charcoal-400 font-mono">/100</span>
            </div>
            <p className="text-sm text-charcoal-500 mt-1">
              {report.checks_passing ?? 0} of {report.checks_total ?? 0} checks passing
            </p>
          </div>
          <div className={`flex items-center gap-2 px-4 py-2 rounded-full border ${SCORE_STYLES[report.overall_status] || SCORE_STYLES.info}`}>
            <span className={`w-2 h-2 rounded-full ${
              report.overall_status === "green" ? "bg-emerald-500" :
              report.overall_status === "amber" ? "bg-amber-500" :
              report.overall_status === "red" ? "bg-red-500" : "bg-charcoal-300"
            }`} />
            <span className="text-sm font-medium capitalize">{report.overall_status}</span>
          </div>
        </section>

        {report.ai_summary && (
          <section className="mt-6 rounded-2xl bg-charcoal-900 text-white p-6 sm:p-7" data-testid="public-ai-summary">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="w-4 h-4 text-emerald-400" />
              <p className="text-[11px] font-mono uppercase tracking-[0.15em] text-white/50">Mission Watch Summary</p>
            </div>
            <p className="text-[15px] leading-relaxed text-white/90">{report.ai_summary}</p>
          </section>
        )}

        {report.recommendations?.length > 0 && (
          <section className="mt-6 rounded-2xl bg-amber-50/70 border border-amber-100 p-6" data-testid="public-recommendations">
            <div className="flex items-center gap-2 mb-3">
              <Lightbulb className="w-4 h-4 text-amber-600" />
              <p className="text-[11px] font-mono uppercase tracking-[0.15em] text-amber-700">Recommended Improvements</p>
            </div>
            <ul className="space-y-2.5">
              {report.recommendations.map((r, i) => (
                <li key={i} className="flex items-start gap-2.5 text-sm text-charcoal-700 leading-relaxed">
                  <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-amber-500 shrink-0" />
                  <span>{r}</span>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* categorized checks */}
        <section className="mt-6 rounded-2xl bg-white border border-black/5 shadow-soft p-6 sm:p-7">
          <p className="text-[11px] font-mono uppercase tracking-[0.15em] text-charcoal-400 mb-4">Checks by Category</p>
          <div className="space-y-6">
            {(report.categories?.length ? report.categories : [{ key: null, name: "Checks", status: report.overall_status }]).map((cat) => {
              const catChecks = cat.key
                ? (report.checks || []).filter((c) => c.category === cat.key)
                : (report.checks || []);
              if (!catChecks.length) return null;
              return (
                <div key={cat.key || "all"}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className={`w-2 h-2 rounded-full ${cat.status === "green" ? "bg-emerald-500" : cat.status === "amber" ? "bg-amber-500" : cat.status === "red" ? "bg-red-500" : "bg-charcoal-300"}`} />
                      <p className="text-[11px] font-mono uppercase tracking-[0.14em] text-charcoal-500">{cat.name}</p>
                    </div>
                    {cat.total != null && (
                      <span className="text-[11px] font-mono text-charcoal-400">{cat.passing}/{cat.total} passing</span>
                    )}
                  </div>
                  <div className="grid sm:grid-cols-2 gap-2">
                    {catChecks.map((c) => (
                      <div key={c.key} className="flex items-start gap-3 py-2.5 px-3.5 rounded-xl border border-black/5 bg-charcoal-50/70">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-charcoal-800 leading-tight">{c.label}</p>
                          <p className="text-[11px] font-mono text-charcoal-400 mt-0.5 truncate">{c.detail}</p>
                        </div>
                        <StatusIcon status={c.status} />
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* footer with disclosure & CTA */}
        <section className="mt-8 rounded-2xl bg-gradient-to-br from-charcoal-900 to-charcoal-800 text-white p-6 sm:p-8 relative overflow-hidden">
          <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-brand/20 blur-3xl pointer-events-none" />
          <div className="relative flex flex-wrap items-center justify-between gap-4">
            <div className="max-w-lg">
              <p className="text-[10px] font-mono uppercase tracking-[0.18em] text-emerald-300">Mission Watch</p>
              <h3 className="mt-1 text-lg sm:text-xl font-semibold tracking-tight">
                Automated digital health checkups for small nonprofits.
              </h3>
              <p className="mt-2 text-sm text-white/60 leading-relaxed">
                We check every donation link, website page, contact form, and SSL certificate —
                and alert the moment anything breaks. Over 30 donation platforms supported.
              </p>
            </div>
            <Link to="/" className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-xl bg-brand text-white text-sm font-semibold hover:bg-brand-hover transition-colors shadow-soft" data-testid="public-cta">
              Try Mission Watch <ExternalLink className="w-3.5 h-3.5" />
            </Link>
          </div>
          <div className="relative mt-5 pt-5 border-t border-white/10 flex flex-wrap items-center justify-between gap-3 text-[11px] font-mono text-white/50">
            <div className="flex items-center gap-1.5">
              <Clock className="w-3 h-3" /> Generated {fmt(report.generated_at)}
            </div>
            <span>Shared {fmt(report.shared_at)}</span>
          </div>
        </section>
      </main>
    </div>
  );
}
